import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Box, 
  Grid, 
  Card, 
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  Pagination
} from '@mui/material';
import { 
  Visibility as ViewIcon, 
  Edit as EditIcon, 
  Close as CloseIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Issue {
  issue_id: string;
  title: string;
  description: string;
  issue_category: string;
  issue_type: string;
  severity: string;
  status: string;
  asset_name?: string;
  asset_type?: string;
  location_name?: string;
  created_by_name?: string;
  resolved_by_name?: string;
  original_response?: string;
  current_response?: string;
  question_text?: string;
  created_at: string;
  updated_at: string;
  resolution_notes?: string;
}

interface IssueStats {
  total_issues: number;
  new_issues: number;
  under_review: number;
  resolved_issues: number;
  critical_issues: number;
  high_issues: number;
  loan_return_issues: number;
  issues_this_week: number;
}

const IssueManagement: React.FC = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [stats, setStats] = useState<IssueStats | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    severity: '',
    asset_id: ''
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    status: '',
    severity: '',
    resolution_notes: ''
  });

  const fetchIssues = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });

      const response = await axios.get(`${API_BASE_URL}/issues?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIssues(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setError(null);
    } catch (err: any) {
      setError('Failed to fetch issues');
      console.error('Error fetching issues:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/issues/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchIssues();
    fetchStats();
  }, [fetchIssues, fetchStats]);

  const handleViewIssue = async (issueId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/issues/${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedIssue(response.data.data);
      setOpenDialog(true);
    } catch (err) {
      console.error('Error fetching issue details:', err);
    }
  };

  const handleEditIssue = (issue: Issue) => {
    setSelectedIssue(issue);
    setEditForm({
      status: issue.status,
      severity: issue.severity,
      resolution_notes: issue.resolution_notes || ''
    });
    setEditDialog(true);
  };

  const handleUpdateIssue = async () => {
    if (!selectedIssue) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/issues/${selectedIssue.issue_id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditDialog(false);
      fetchIssues();
      fetchStats();
    } catch (err) {
      console.error('Error updating issue:', err);
      setError('Failed to update issue');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW': return 'error';
      case 'UNDER_REVIEW': return 'warning';
      case 'RESOLVED': return 'success';
      case 'DISMISSED': return 'default';
      default: return 'default';
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filtering
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Issue Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Issues
                </Typography>
                <Typography variant="h5">
                  {stats.total_issues}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  New Issues
                </Typography>
                <Typography variant="h5" color="error">
                  {stats.new_issues}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Critical/High
                </Typography>
                <Typography variant="h5" color="warning">
                  {stats.critical_issues + stats.high_issues}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Week
                </Typography>
                <Typography variant="h5">
                  {stats.issues_this_week}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="NEW">New</MenuItem>
                <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                <MenuItem value="RESOLVED">Resolved</MenuItem>
                <MenuItem value="DISMISSED">Dismissed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LOAN_RETURN">Loan Return</MenuItem>
                <MenuItem value="SAFETY_CHECK">Safety Check</MenuItem>
                <MenuItem value="ASSET_CONDITION">Asset Condition</MenuItem>
                <MenuItem value="COMPLIANCE">Compliance</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                label="Severity"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setFilters({ status: '', category: '', severity: '', asset_id: '' });
                  setPage(1);
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Issues Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Asset</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Loading...</TableCell>
              </TableRow>
            ) : issues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No issues found</TableCell>
              </TableRow>
            ) : (
              issues.map((issue) => (
                <TableRow key={issue.issue_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {issue.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {issue.description.length > 100 
                        ? `${issue.description.substring(0, 100)}...` 
                        : issue.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={issue.issue_category.replace('_', ' ')} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={issue.severity} 
                      color={getSeverityColor(issue.severity) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={issue.status.replace('_', ' ')} 
                      color={getStatusColor(issue.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {issue.asset_name ? (
                      <div>
                        <Typography variant="body2">{issue.asset_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {issue.asset_type} - {issue.location_name}
                        </Typography>
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(issue.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {issue.created_by_name || 'System'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewIssue(issue.issue_id)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Issue">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditIssue(issue)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* View Issue Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Issue Details
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedIssue && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">{selectedIssue.title}</Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {selectedIssue.description}
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="subtitle2">Category:</Typography>
                <Typography variant="body2">{selectedIssue.issue_category.replace('_', ' ')}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="subtitle2">Type:</Typography>
                <Typography variant="body2">{selectedIssue.issue_type.replace('_', ' ')}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="subtitle2">Severity:</Typography>
                <Chip 
                  label={selectedIssue.severity} 
                  color={getSeverityColor(selectedIssue.severity) as any}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="subtitle2">Status:</Typography>
                <Chip 
                  label={selectedIssue.status.replace('_', ' ')} 
                  color={getStatusColor(selectedIssue.status) as any}
                  size="small"
                />
              </Grid>

              {selectedIssue.asset_name && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Asset:</Typography>
                    <Typography variant="body2">
                      {selectedIssue.asset_name} ({selectedIssue.asset_type}) - {selectedIssue.location_name}
                    </Typography>
                  </Grid>
                </>
              )}

              {selectedIssue.question_text && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Question:</Typography>
                  <Typography variant="body2">{selectedIssue.question_text}</Typography>
                </Grid>
              )}

              {selectedIssue.original_response && selectedIssue.current_response && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Original Response:</Typography>
                    <Typography variant="body2" color="success.main">
                      {selectedIssue.original_response}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Current Response:</Typography>
                    <Typography variant="body2" color="error.main">
                      {selectedIssue.current_response}
                    </Typography>
                  </Grid>
                </>
              )}

              {selectedIssue.resolution_notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Resolution Notes:</Typography>
                  <Typography variant="body2">{selectedIssue.resolution_notes}</Typography>
                </Grid>
              )}

              <Grid item xs={6}>
                <Typography variant="subtitle2">Created:</Typography>
                <Typography variant="body2">
                  {new Date(selectedIssue.created_at).toLocaleString()}
                  {selectedIssue.created_by_name && ` by ${selectedIssue.created_by_name}`}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2">Last Updated:</Typography>
                <Typography variant="body2">
                  {new Date(selectedIssue.updated_at).toLocaleString()}
                  {selectedIssue.resolved_by_name && ` by ${selectedIssue.resolved_by_name}`}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Issue Dialog */}
      <Dialog 
        open={editDialog} 
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Issue</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="NEW">New</MenuItem>
                  <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                  <MenuItem value="RESOLVED">Resolved</MenuItem>
                  <MenuItem value="DISMISSED">Dismissed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={editForm.severity}
                  onChange={(e) => setEditForm(prev => ({ ...prev, severity: e.target.value }))}
                  label="Severity"
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="CRITICAL">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Resolution Notes"
                value={editForm.resolution_notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, resolution_notes: e.target.value }))}
                placeholder="Add notes about the resolution or action taken..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateIssue} variant="contained">
            Update Issue
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default IssueManagement; 